/**
 * What the field-staff web home shows, as plain functions (2026-10-07). Every
 * input is a self-scoped API answer: the person's own visits, forms, clients,
 * time and messages. Nothing here reads about other clients or other staff.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MS = 24 * 60 * 60 * 1000;

const plural = (n, one, many) => (n === 1 ? one : many);

/** Local 12-hour clock time ('2:05 PM') for a timestamp; null when missing. */
export function clockTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const hours = d.getHours();
  const period = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${period}`;
}

/** '10:00 AM – 2:00 PM', or whichever end is known. */
export function timeRange(start, end) {
  const from = clockTime(start);
  const to = clockTime(end);
  if (from && to) return `${from} – ${to}`;
  return from || to || 'No time set';
}

const STATUS = [
  { key: 'missed', label: 'Missed', tone: 'missed' },
  { key: 'done', label: 'Done', tone: 'done' },
  { key: 'inProgress', label: 'In progress', tone: 'active' },
  { key: 'notStarted', label: 'Not started', tone: 'upcoming' },
];

/**
 * Today's own visits from GET /dashboard/lists/hh-today, in time order, with
 * the next one still to happen. Null when the list did not load.
 */
export function todayVisits(hh) {
  if (!hh) return null;
  const rows = STATUS.flatMap(({ key, label, tone }) => (hh[key] || []).map((visit) => {
    const start = visit.scheduledStart || visit.checkIn || null;
    return {
      id: visit.id || visit.scheduledVisitId,
      client: visit.patientName || 'Client',
      time: timeRange(start, visit.scheduledEnd || visit.checkOut),
      service: [visit.serviceCode, visit.serviceName].filter(Boolean).join(' ') || null,
      status: label,
      tone,
      start,
    };
  }));
  rows.sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));
  const counts = hh.counts || {};
  return {
    rows,
    done: counts.done ?? (hh.done || []).length,
    total: counts.scheduled ?? rows.length,
    next: rows.find((row) => row.tone === 'active' || row.tone === 'upcoming') || null,
  };
}

// Local month and day of a visit timestamp ('Sep 22').
function visitDay(visit) {
  const d = new Date(visit?.checkOut || visit?.checkIn || visit?.scheduledStart || '');
  return Number.isNaN(d.getTime()) ? null : `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// 'Sep 22', 'Sep 22 and Sep 24', or 'Sep 22, Sep 24 and 3 more' when the count is larger.
function joinWithMore(names, total) {
  const shown = names.slice(0, 2);
  const more = Math.max(0, total - shown.length);
  if (more > 0) return `${shown.join(', ')} and ${more} more`;
  return shown.length === 2 ? `${shown[0]} and ${shown[1]}` : shown[0] || '';
}

const visitDays = (visits) => [...new Set((Array.isArray(visits) ? visits : []).map(visitDay).filter(Boolean))];

/** A system flag reason in plain words (the full reason stays with the supervisor). */
export function friendlyFlagReason(reason) {
  const text = String(reason || '');
  if (/checkout location|check-out location|miles from/i.test(text)) return 'checked out away from the client’s home';
  if (/auto-stopped|automatically stopped/i.test(text)) return 'the visit was stopped automatically';
  if (/manual entry/i.test(text)) return 'entered by hand, without EVV';
  return 'your supervisor will review it';
}

function unsignedDetail(count, visits) {
  const days = visitDays(visits);
  const when = days.length ? joinWithMore(days, Math.max(count, days.length)) : 'Last 30 days';
  return `${when} · get the signature in the app`;
}

function flaggedDetail(count, visits) {
  const list = Array.isArray(visits) ? visits : [];
  const days = visitDays(list);
  if (!days.length) return 'Your supervisor will review it';
  if (count === 1 && list.length === 1) return `${days[0]} · ${friendlyFlagReason(list[0].flagReason)}`;
  return `${joinWithMore(days, Math.max(count, days.length))} · your supervisor will review them`;
}

function formsDetail(count, forms) {
  const titles = (Array.isArray(forms) ? forms : []).map((f) => f?.title).filter(Boolean);
  if (!titles.length) return 'Sign in the app';
  const more = Math.max(0, count - 1);
  return `${titles[0]}${more > 0 ? ` and ${more} more` : ''} · sign in the app`;
}

/**
 * The "Needs you" rows: only counts above zero. Each names its visits' dates,
 * the flag reason or the form when the first few items are known.
 */
export function needsItems({
  unsigned = 0, flagged = 0, forms = 0, unsignedVisits = [], flaggedVisits = [], pendingForms = [],
} = {}) {
  return [
    {
      key: 'unsigned', count: unsigned, tone: 'amber',
      title: plural(unsigned, 'Visit missing a client signature', 'Visits missing a client signature'),
      detail: unsignedDetail(unsigned, unsignedVisits),
    },
    {
      key: 'flagged', count: flagged, tone: 'red',
      title: plural(flagged, 'Visit flagged for review', 'Visits flagged for review'),
      detail: flaggedDetail(flagged, flaggedVisits),
    },
    {
      key: 'forms', count: forms, tone: 'blue',
      title: plural(forms, 'Form waiting for your signature', 'Forms waiting for your signature'),
      detail: formsDetail(forms, pendingForms),
    },
  ].filter((item) => Number(item.count) > 0);
}

// A 'YYYY-MM-DD' pay-period date at local noon, so no timezone moves the day.
const calendarDay = (ymd) => new Date(`${ymd}T12:00:00`);
const shortDate = (date) => `${MONTHS[date.getMonth()]} ${date.getDate()}`;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** This pay period's range, the person's hours and visits, and days left after today. */
export function payPeriodSummary(period, totals, today = new Date()) {
  if (!period?.start || !period?.end) return null;
  const start = calendarDay(period.start);
  const end = calendarDay(period.end);
  const daysLeft = Math.max(0, Math.round((startOfDay(end) - startOfDay(today)) / DAY_MS));
  return {
    range: `${shortDate(start)} – ${shortDate(end)}`,
    hours: Number(totals?.totalHours) || 0,
    visits: Number(totals?.totalVisits) || 0,
    daysLeft,
  };
}

/** Own balances by leave type, and own requests still waiting for a decision. */
export function timeOffSummary(balances, requests) {
  const balanceRows = (Array.isArray(balances) ? balances : []).map((b) => ({
    label: b.leaveType?.label || b.policy?.name || 'Time off',
    hours: Math.round((Number(b.currentBalance) || 0) * 100) / 100,
  }));
  const pending = (Array.isArray(requests) ? requests : [])
    .filter((r) => r.status === 'pending')
    .map((r) => {
      // Leave dates are calendar days stored at noon UTC: read them in UTC.
      const d = new Date(r.startDate);
      return {
        id: r.id,
        label: r.leaveType?.label || 'Time off',
        when: Number.isNaN(d.getTime()) ? '' : `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`,
        hours: Number(r.hoursRequested) || 0,
      };
    });
  return { balances: balanceRows, pending };
}

const conversationsOf = (response) => response?.conversations || (Array.isArray(response) ? response : []);

/** Unread messages across the person's own conversations. */
export function unreadTotal(response) {
  return conversationsOf(response).reduce((sum, c) => sum + (Number(c?.unreadCount) || 0), 0);
}

/**
 * Unread count and who it is from: the other person's name for one
 * conversation with one or two others, the conversation's title for a group,
 * or how many conversations.
 */
export function messageSummary(response, myUserId) {
  const unreadConversations = conversationsOf(response).filter((c) => Number(c?.unreadCount) > 0);
  const unread = unreadTotal(response);
  if (!unread) return { unread: 0, text: 'No unread messages' };
  if (unreadConversations.length > 1) return { unread, text: `${unread} unread in ${unreadConversations.length} conversations` };
  const [conv] = unreadConversations;
  const others = (conv?.participants || []).filter((p) => p?.id !== myUserId).map((p) => p?.name).filter(Boolean);
  if (others.length >= 1 && others.length <= 2) return { unread, text: `${unread} unread from ${others.join(' and ')}` };
  return { unread, text: conv?.title ? `${unread} unread in ${conv.title}` : `${unread} unread` };
}

const initialsOf = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('');

/** Assigned clients (GET /patients/assigned) with their next visit today, if any. */
export function clientRows(patients, today) {
  return (Array.isArray(patients) ? patients : []).map((p) => {
    const name = p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Client';
    const visit = today?.rows?.find((row) => row.client === name && (row.tone === 'upcoming' || row.tone === 'active'));
    return {
      id: p.id,
      name,
      initials: initialsOf(name),
      next: visit ? `Next visit today at ${clockTime(visit.start)}` : 'No visit today',
    };
  });
}
